import { Box } from '@mui/material';
import { useDrop } from 'react-dnd';
import type { DropTargetMonitor } from 'react-dnd';
import { useState, useEffect, useMemo, useRef } from 'react';
import ShortUniqueId from 'short-unique-id';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { TAlert } from '../../../types/types';
import EmailCard from './EmailCard'; // Adjust import path
import { useDispatch } from 'react-redux';
import {
  updateColumnSecurityFilters,
  removeColumnSecurityCard,
  updateUserSelectionColumn,
  callQueryAPI,
} from '../../../redux/ChartPoperties/ChartControlsActions';

interface IDropZone {
  propKey: string;
  chartControls: any;
  chartProperties: any;
}

interface EmailCardData {
  id: string;
  name: string;
}

interface DragItem {
  type: 'card' | 'calculation';
  boxUUID?: string;
  bIndex?: number;
  fieldData?: {
    dataType?: string;
    fieldname?: string;
    displayname?: string;
    tableId?: string;
  };
}

interface CollectedProps {
  isOver: boolean;
  canDrop: boolean;
}

const EmailDropZone = ({ propKey, chartControls, chartProperties }: IDropZone) => {
  const [alert, setAlert] = useState<null | TAlert>(null);
  const [cards, setCards] = useState<EmailCardData[]>([]);

  const currentOperator = useMemo(() => {
    return chartControls?.properties[propKey]?.columnSecurity?.currentOperator?.toLowerCase();
  }, [chartControls?.properties[propKey]?.columnSecurity?.currentOperator]);

  const dispatch = useDispatch();

  const uid = new ShortUniqueId({ length: 4 });
  const uniqueId = uid();

  const isFirstRender = useRef(true);

  const userSelection = useMemo(() => {
    return (
      chartControls?.properties?.[propKey]?.rowSecurity?.filterPanels?.[currentOperator]
        ?.filters?.[0]?.userSelection || []
    );
  }, [
    chartControls?.properties[propKey]?.rowSecurity?.filterPanels[currentOperator]?.filters?.[0]
      ?.userSelection,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(callQueryAPI(propKey, true));
  }, [
    chartControls.properties[propKey]?.columnSecurity?.filterPanels[currentOperator]?.filters,
    chartControls.properties[propKey]?.columnSecurity?.filterPanels[currentOperator],
  ]);

  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, CollectedProps>({
    accept: ['card', 'calculation'],
    canDrop: (item) => item.bIndex === 99,
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: DragItem) => {
      const handleDrop = async () => {
        const dataType = item.fieldData?.dataType?.toLowerCase();
        const fieldName = item.fieldData?.fieldname?.toLowerCase();
        const displayName = item.fieldData?.displayname?.toLowerCase();

        const isEmailLike =
          dataType === 'email' || fieldName?.includes('email') || displayName?.includes('email');

        const isCalculationValid =
          item.type === 'calculation' && ['email', 'string', 'text'].includes(dataType || '');

        if (item.type === 'card' && !isEmailLike) {
          setAlert({
            message: 'Only fields related to emails can be dropped here!',
            severity: 'error',
          });
          return;
        }

        if (item.type === 'calculation' && !isCalculationValid) {
          setAlert({
            message: 'Only flows related to emails can be dropped here!',
            severity: 'error',
          });
          return;
        }

        const filterPanels =
          chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator];
        const existingFilters = filterPanels?.filters || [];

        const isDuplicate = existingFilters.some(
          (f: any) => f.fieldName === item.fieldData?.fieldname,
        );

        if (isDuplicate) return;

        const filteredData = {
          dataType: item.fieldData?.dataType,
          displayName: item.fieldData?.displayname,
          fieldName: item.fieldData?.fieldname,
          source: uniqueId || '',
          sourceType: item.type || '',
          tableId: item.fieldData?.tableId,
          userSelection: userSelection,
          calculationUUID: item.boxUUID,
          mapping: [],
          filterType: `column_level_security_${currentOperator?.toLowerCase()}`,
          operator: 'in',
          shouldExclude: false,
          // measureDropFields: [],
        };

        dispatch(updateColumnSecurityFilters(propKey, filteredData, currentOperator));
        const newCard: EmailCardData = {
          id: uniqueId,
          name: item.fieldData?.displayname || '',
        };

        setCards([newCard]);
      };

      handleDrop();
    },
  });

  const handleDelete = (id: string) => {
    dispatch(updateUserSelectionColumn(propKey, currentOperator?.toLowerCase(), []));
    dispatch(removeColumnSecurityCard(propKey, id, currentOperator));
    setCards((prevCards) => prevCards.filter((card) => card.id !== id));
    // fetchAllData();
    dispatch(callQueryAPI(propKey, true));
  };

  useEffect(() => {
    const filterObj =
      chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[currentOperator]
        ?.filters?.[0];

    const cardsFromRedux = filterObj
      ? [
          {
            id: filterObj.source,
            name: filterObj.displayName,
          },
        ]
      : [];

    setCards(cardsFromRedux);
  }, [chartControls?.properties?.[propKey]?.columnSecurity, propKey, currentOperator]);

  return (
    <Box
      ref={drop}
      sx={{
        padding: 1,
        minHeight: '60px',
        border: isOver && canDrop? '1px dashed #2bb9bb' : 'none',
        backgroundColor: isOver && canDrop? 'rgba(43, 185, 187, 0.05)' : 'inherit',
      }}
    >
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}

      {cards.length > 0 ? (
        cards.map((card) => (
          <EmailCard
            key={card.id}
            id={card.id}
            name={card.name}
            handleDelete={() => handleDelete(card.id)}
            chartControls={chartControls}
            propKey={propKey}
          />
        ))
      ) : (
        <span>Drag and Drop an email field or calculation</span>
      )}
    </Box>
  );
};

export default EmailDropZone;
